import { useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../lib/data';
import { useSelection, type Selection } from '../lib/selection';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Client, Project, Task } from '../lib/database.types';

function selectionKey(s: Selection): string {
  switch (s.type) {
    case 'client':
      return `c:${s.slug}`;
    case 'project':
      return `p:${s.slug}`;
    case 'task':
      return `t:${s.id}`;
    default:
      return s.type;
  }
}

const ACTIVE_STATUSES = new Set(['pending', 'in_progress', 'blocked']);
// Builds in these states are hidden from the sidebar by default. Toggle the
// "show archived" button to reveal them.
const HIDDEN_STATES = new Set(['archived', 'completed']);

export function Sidebar() {
  const {
    clients,
    projects,
    tasks,
    tasksByProject,
    childrenByParent,
    loading,
    projectBySlug,
    subClientsByParent,
    isTopLevelClient,
  } = useData();
  const {
    selection,
    select,
    toggleExpanded,
    isExpanded,
    expandChain,
    showArchived,
    toggleShowArchived,
  } = useSelection();

  // Auto-expand the parent chain when the selection changes to a DIFFERENT
  // node. If the user clicks the same company row twice (to toggle), the
  // selection key is unchanged and we skip re-expansion — so toggleExpanded
  // can actually close it.
  const prevKey = useRef<string>('');
  useEffect(() => {
    const key = selectionKey(selection);
    if (key === prevKey.current) return;
    prevKey.current = key;

    if (selection.type === 'project') {
      const p = projectBySlug[selection.slug];
      if (p?.client_slug) expandChain([`client:${p.client_slug}`, `project:${p.slug}`]);
    } else if (selection.type === 'task') {
      const task = tasks.find((t) => t.id === selection.id);
      if (!task) return;
      const p = projectBySlug[task.project_slug];
      const keys: string[] = [`project:${task.project_slug}`];
      if (p?.client_slug) keys.push(`client:${p.client_slug}`);
      let cursor = task;
      while (cursor.parent_task_id) {
        keys.push(`task:${cursor.parent_task_id}`);
        const parent = tasks.find((t) => t.id === cursor.parent_task_id);
        if (!parent) break;
        cursor = parent;
      }
      expandChain(keys);
    } else if (selection.type === 'client') {
      expandChain([`client:${selection.slug}`]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, projectBySlug, tasks]);

  const activeTaskCount = useMemo(
    () => tasks.filter((t) => ACTIVE_STATUSES.has(t.status) && t.parent_task_id === null).length,
    [tasks],
  );

  const myActiveCount = useMemo(
    () =>
      tasks.filter(
        (t) =>
          ACTIVE_STATUSES.has(t.status) &&
          t.parent_task_id === null &&
          (t.assigned_to === 'me' ||
            (!t.assigned_to && (t.execution_target === 'me' || !t.execution_target))),
      ).length,
    [tasks],
  );

  const completedCount = useMemo(
    () => tasks.filter((t) => t.status === 'completed').length,
    [tasks],
  );

  const visibleProjects = useMemo(
    () => (showArchived ? projects : projects.filter((p) => !HIDDEN_STATES.has(p.state))),
    [projects, showArchived],
  );

  const hiddenCount = projects.length - visibleProjects.length;

  // Projects with a client_slug → COMPANIES section (grouped by client).
  // Projects with client_slug=NULL → AUTOMATIONS section (grouped by category).
  const projectsByClient = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of visibleProjects) {
      if (!p.client_slug) continue;
      const arr = map.get(p.client_slug) ?? [];
      arr.push(p);
      map.set(p.client_slug, arr);
    }
    return map;
  }, [visibleProjects]);

  const automationsByCategory = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of visibleProjects) {
      if (p.client_slug) continue;
      const key = p.category || 'other';
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return map;
  }, [visibleProjects]);

  const activeByProject = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [slug, ts] of tasksByProject.entries()) {
      counts.set(slug, ts.filter((t) => ACTIVE_STATUSES.has(t.status)).length);
    }
    return counts;
  }, [tasksByProject]);

  const activeByClient = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of clients) {
      const projs = projectsByClient.get(c.slug) ?? [];
      let sum = 0;
      for (const p of projs) sum += activeByProject.get(p.slug) ?? 0;
      counts.set(c.slug, sum);
    }
    return counts;
  }, [clients, projectsByClient, activeByProject]);

  return (
    <aside
      className="w-[260px] shrink-0 border-r border-border bg-panel/60 backdrop-blur
                 flex flex-col h-full overflow-hidden"
    >
      <div className="px-4 py-4 border-b border-border flex items-center justify-between">
        <div className="font-mono text-xs tracking-widest text-amberHi">◆ TASK BOARD</div>
        {hiddenCount > 0 || showArchived ? (
          <button
            onClick={toggleShowArchived}
            className="text-[10px] font-mono text-dim hover:text-ink"
            title={showArchived ? 'hide archived builds' : 'show archived builds'}
          >
            {showArchived ? '− hide archived' : `+ ${hiddenCount} archived`}
          </button>
        ) : null}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 text-sm space-y-0.5">
        <SidebarItem
          selected={selection.type === 'home'}
          onClick={() => select({ type: 'home' })}
          icon="◉"
          label="Home"
          count={activeTaskCount}
        />
        <SidebarItem
          selected={selection.type === 'my-tasks'}
          onClick={() => select({ type: 'my-tasks' })}
          icon="◎"
          label="My Tasks"
          count={myActiveCount}
        />
        <SidebarItem
          selected={selection.type === 'uptime'}
          onClick={() => select({ type: 'uptime' })}
          icon="◇"
          label="Uptime"
        />
        <SidebarItem
          selected={selection.type === 'deleted'}
          onClick={() => select({ type: 'deleted' })}
          icon="🗑"
          label="Trash"
        />
        <SidebarItem
          selected={selection.type === 'completed'}
          onClick={() => select({ type: 'completed' })}
          icon="✓"
          label="Completed"
          count={completedCount}
        />
        <SidebarItem
          selected={selection.type === 'contacts'}
          onClick={() => select({ type: 'contacts' })}
          icon="◉"
          label="Contacts"
        />
        <SidebarItem
          selected={selection.type === 'pipelines'}
          onClick={() => select({ type: 'pipelines' })}
          icon="▸"
          label="Pipelines"
        />

        <SectionLabel>COMPANIES</SectionLabel>

        {loading && (
          <div className="px-3 py-2 text-xs text-dim font-mono">loading…</div>
        )}

        {!loading &&
          clients
            .filter(isTopLevelClient)
            .filter(
              (c) =>
                c.slug !== 'shwetzer' ||
                (projectsByClient.get(c.slug) ?? []).length > 0 ||
                (subClientsByParent.get(c.slug) ?? []).length > 0,
            )
            .map((c) => {
              const clientProjects = projectsByClient.get(c.slug) ?? [];
              const subClients = subClientsByParent.get(c.slug) ?? [];
              const isOpen = isExpanded(`client:${c.slug}`);
              const isSelected = selection.type === 'client' && selection.slug === c.slug;
              return (
                <ClientNode
                  key={c.slug}
                  client={c}
                  projects={clientProjects}
                  subClients={subClients}
                  isOpen={isOpen}
                  isSelected={isSelected}
                  selection={selection}
                  activeByProject={activeByProject}
                  activeCount={activeByClient.get(c.slug) ?? 0}
                  tasksByProject={tasksByProject}
                  childrenByParent={childrenByParent}
                  projectsByClient={projectsByClient}
                  subClientsByParent={subClientsByParent}
                  onToggle={() => toggleExpanded(`client:${c.slug}`)}
                  onSelect={(s) => select(s)}
                  isExpanded={isExpanded}
                  toggleExpanded={toggleExpanded}
                  depth={0}
                />
              );
            })}

        {!loading && automationsByCategory.size > 0 && (
          <>
            <SectionLabel>AUTOMATIONS</SectionLabel>
            {Array.from(automationsByCategory.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, projs]) => (
                <CategoryNode
                  key={category}
                  category={category}
                  projects={projs}
                  selection={selection}
                  activeByProject={activeByProject}
                  tasksByProject={tasksByProject}
                  childrenByParent={childrenByParent}
                  isExpanded={isExpanded}
                  toggleExpanded={toggleExpanded}
                  onSelect={(s) => select(s)}
                />
              ))}
          </>
        )}
      </nav>
    </aside>
  );
}

function CategoryNode({
  category,
  projects,
  selection,
  activeByProject,
  tasksByProject,
  childrenByParent,
  isExpanded,
  toggleExpanded,
  onSelect,
}: {
  category: string;
  projects: Project[];
  selection: Selection;
  activeByProject: Map<string, number>;
  tasksByProject: Map<string, Task[]>;
  childrenByParent: Map<string, Task[]>;
  isExpanded: (key: string) => boolean;
  toggleExpanded: (key: string) => void;
  onSelect: (s: Selection) => void;
}) {
  const key = `category:${category}`;
  const isOpen = isExpanded(key);
  const activeTotal = projects.reduce(
    (sum, p) => sum + (activeByProject.get(p.slug) ?? 0),
    0,
  );
  return (
    <div>
      <div
        className="group flex items-center gap-1 rounded-md hover:bg-panelHi border border-transparent"
      >
        <button
          aria-label={isOpen ? 'collapse' : 'expand'}
          onClick={() => toggleExpanded(key)}
          className="pl-2 py-2 text-dim hover:text-ink shrink-0"
        >
          <span className={'inline-block transition-transform ' + (isOpen ? 'rotate-90' : '')}>
            ▸
          </span>
        </button>
        <button
          onClick={() => toggleExpanded(key)}
          className="flex-1 flex items-center gap-2 py-2 pr-3 text-left min-w-0 text-ink"
        >
          <span className="truncate text-sm capitalize">{category}</span>
          {activeTotal > 0 && (
            <span className="ml-auto text-xs text-dim font-mono tabular-nums">{activeTotal}</span>
          )}
        </button>
      </div>
      {isOpen && (
        <div className="ml-4 border-l border-border pl-2 mt-0.5 space-y-0.5">
          {projects.map((p) => {
            const pSelected = selection.type === 'project' && selection.slug === p.slug;
            const pKey = `project:${p.slug}`;
            const pOpen = isExpanded(pKey);
            const topTasks = tasksByProject.get(p.slug) ?? [];
            const activeTopTasks = topTasks.filter((t) =>
              ACTIVE_STATUSES.has(t.status),
            );
            return (
              <div key={p.slug}>
                <div
                  className={
                    'group flex items-center gap-1 rounded-md transition-colors ' +
                    (pSelected
                      ? 'bg-amber/15 border border-amber/60'
                      : 'hover:bg-panelHi border border-transparent')
                  }
                >
                  <button
                    aria-label={pOpen ? 'collapse' : 'expand'}
                    onClick={() => toggleExpanded(pKey)}
                    className="pl-1.5 py-1.5 text-dim hover:text-ink shrink-0 text-xs"
                  >
                    <span
                      className={
                        'inline-block transition-transform ' + (pOpen ? 'rotate-90' : '')
                      }
                    >
                      ▸
                    </span>
                  </button>
                  <button
                    onClick={() => onSelect({ type: 'project', slug: p.slug })}
                    className={
                      'flex-1 flex items-center gap-2 py-1.5 pr-2 text-left min-w-0 ' +
                      (pSelected ? 'text-white' : 'text-muted')
                    }
                  >
                    <span className="truncate text-xs">{p.name}</span>
                    {(activeByProject.get(p.slug) ?? 0) > 0 && (
                      <span className="ml-auto text-[10px] text-dim font-mono tabular-nums">
                        {activeByProject.get(p.slug)}
                      </span>
                    )}
                  </button>
                </div>
                {pOpen && (
                  <div className="ml-4 border-l border-border/60 pl-2 mt-0.5 space-y-0.5">
                    {activeTopTasks.length === 0 && (
                      <div className="text-[10px] text-dim italic py-1 px-2">no active</div>
                    )}
                    {activeTopTasks.map((t) => (
                      <TaskLeaf
                        key={t.id}
                        task={t}
                        selection={selection}
                        childrenByParent={childrenByParent}
                        onSelect={onSelect}
                        depth={0}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-5 pb-1 text-[10px] font-mono tracking-widest text-dim uppercase">
      {children}
    </div>
  );
}

function SidebarItem({
  selected,
  onClick,
  icon,
  label,
  count,
}: {
  selected: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors ' +
        (selected
          ? 'bg-amber/15 text-white border border-amber/60'
          : 'text-muted hover:bg-panelHi hover:text-ink border border-transparent')
      }
    >
      <span className="text-amberHi w-4 text-center">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="text-xs text-dim font-mono tabular-nums">{count}</span>
      )}
    </button>
  );
}

function ClientNode({
  client,
  projects,
  subClients,
  isOpen,
  isSelected,
  selection,
  activeByProject,
  activeCount,
  tasksByProject,
  childrenByParent,
  projectsByClient,
  subClientsByParent,
  onToggle,
  onSelect,
  isExpanded,
  toggleExpanded,
  depth,
}: {
  client: Client;
  projects: Project[];
  subClients: Client[];
  isOpen: boolean;
  isSelected: boolean;
  selection: Selection;
  activeByProject: Map<string, number>;
  activeCount: number;
  tasksByProject: Map<string, Task[]>;
  childrenByParent: Map<string, Task[]>;
  projectsByClient: Map<string, Project[]>;
  subClientsByParent: Map<string, Client[]>;
  onToggle: () => void;
  onSelect: (s: Selection) => void;
  isExpanded: (key: string) => boolean;
  toggleExpanded: (key: string) => void;
  depth: number;
}) {
  return (
    <div>
      <div
        onClick={() => {
          // Click the row = toggle expand AND select (navigate to detail).
          // If already selected and expanded, collapse.
          onToggle();
          onSelect({ type: 'client', slug: client.slug });
        }}
        className={
          'group flex items-center gap-1 rounded-md cursor-pointer transition-colors ' +
          (isSelected
            ? 'bg-amber/15 border border-amber/60'
            : 'hover:bg-panelHi border border-transparent')
        }
      >
        <span
          aria-label={isOpen ? 'collapsed' : 'expanded'}
          className="pl-2 py-2 text-dim shrink-0 w-6 text-center"
        >
          <span className={'inline-block transition-transform ' + (isOpen ? 'rotate-90' : '')}>
            ▸
          </span>
        </span>
        <div
          className={
            'flex-1 flex items-center gap-2 py-2 pr-3 min-w-0 ' +
            (isSelected ? 'text-white' : 'text-ink')
          }
        >
          <span className="truncate text-sm">{client.display_name}</span>
          {activeCount > 0 && (
            <span className="ml-auto text-xs text-dim font-mono tabular-nums">
              {activeCount}
            </span>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="ml-4 border-l border-border pl-2 mt-0.5 space-y-0.5">
          {/* Sub-clients (2-level nesting): e.g. VGP + Kienbaum + iPro under Talent Signals */}
          {subClients.map((sc) => {
            const scProjects = projectsByClient.get(sc.slug) ?? [];
            const scSubSubs = subClientsByParent.get(sc.slug) ?? [];
            const scIsOpen = isExpanded(`client:${sc.slug}`);
            const scIsSelected = selection.type === 'client' && selection.slug === sc.slug;
            const scActive = scProjects.reduce(
              (sum, p) => sum + (activeByProject.get(p.slug) ?? 0),
              0,
            );
            return (
              <ClientNode
                key={sc.slug}
                client={sc}
                projects={scProjects}
                subClients={scSubSubs}
                isOpen={scIsOpen}
                isSelected={scIsSelected}
                selection={selection}
                activeByProject={activeByProject}
                activeCount={scActive}
                tasksByProject={tasksByProject}
                childrenByParent={childrenByParent}
                projectsByClient={projectsByClient}
                subClientsByParent={subClientsByParent}
                onToggle={() => toggleExpanded(`client:${sc.slug}`)}
                onSelect={onSelect}
                isExpanded={isExpanded}
                toggleExpanded={toggleExpanded}
                depth={depth + 1}
              />
            );
          })}
          {projects.length === 0 && subClients.length === 0 && (
            <div className="text-xs text-dim italic py-1 px-2">no projects</div>
          )}
          {projects.map((p) => {
            const pSelected = selection.type === 'project' && selection.slug === p.slug;
            const pKey = `project:${p.slug}`;
            const pOpen = isExpanded(pKey);
            const topTasks = tasksByProject.get(p.slug) ?? [];
            const activeTopTasks = topTasks.filter((t) =>
              ACTIVE_STATUSES.has(t.status),
            );
            return (
              <div key={p.slug}>
                <div
                  onClick={() => {
                    // Click a build row: expand its tasks in place.
                    // Only navigate to the full ProjectView on the dedicated arrow button.
                    toggleExpanded(pKey);
                  }}
                  className={
                    'group flex items-center gap-1 rounded-md cursor-pointer transition-colors ' +
                    (pSelected
                      ? 'bg-amber/15 border border-amber/60'
                      : 'hover:bg-panelHi border border-transparent')
                  }
                >
                  <span
                    aria-label={pOpen ? 'collapsed' : 'expanded'}
                    className="pl-1.5 py-1.5 text-dim shrink-0 text-xs w-5 text-center"
                  >
                    <span
                      className={
                        'inline-block transition-transform ' + (pOpen ? 'rotate-90' : '')
                      }
                    >
                      ▸
                    </span>
                  </span>
                  <div
                    className={
                      'flex-1 flex items-center gap-2 py-1.5 pr-1 min-w-0 ' +
                      (pSelected ? 'text-white' : 'text-muted')
                    }
                  >
                    <span className="truncate text-xs">{p.name}</span>
                    {(activeByProject.get(p.slug) ?? 0) > 0 && (
                      <span className="ml-auto text-[10px] text-dim font-mono tabular-nums">
                        {activeByProject.get(p.slug)}
                      </span>
                    )}
                  </div>
                  {/* Dedicated "open full view" arrow — navigates to ProjectView */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect({ type: 'project', slug: p.slug });
                    }}
                    className="px-1.5 py-1.5 text-dim hover:text-amberHi shrink-0 text-[10px]"
                    aria-label="open build in main pane"
                    title="open full view"
                  >
                    ↗
                  </button>
                </div>

                {pOpen && (
                  <div className="ml-4 border-l border-border/60 pl-2 mt-0.5 space-y-0.5">
                    {activeTopTasks.length === 0 && (
                      <div className="text-[10px] text-dim italic py-1 px-2">no active</div>
                    )}
                    {activeTopTasks.map((t) => (
                      <TaskLeaf
                        key={t.id}
                        task={t}
                        selection={selection}
                        childrenByParent={childrenByParent}
                        onSelect={onSelect}
                        depth={0}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskLeaf({
  task,
  selection,
  childrenByParent,
  onSelect,
  depth,
}: {
  task: Task;
  selection: Selection;
  childrenByParent: Map<string, Task[]>;
  onSelect: (s: Selection) => void;
  depth: number;
}) {
  const selected = selection.type === 'task' && selection.id === task.id;
  const kids = childrenByParent.get(task.id) ?? [];
  const activeKids = kids.filter((k) => ACTIVE_STATUSES.has(k.status));
  const { patchTaskLocal } = useData();
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  const complete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (READONLY) return;
    setBusy(true);
    const now = new Date().toISOString();
    await updateTaskWithRollback({
      task,
      patch: { status: 'completed', completed_at: now },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ status: 'completed', completed_at: now })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task completed',
        body: task.title.slice(0, 60),
        ttlMs: 2500,
      },
      errorTitle: 'Failed to complete task',
    });
    setBusy(false);
  };

  return (
    <div>
      <div
        className={
          'w-full flex items-center gap-1 px-1 py-0.5 rounded transition-colors group ' +
          (selected
            ? 'bg-amber/15 border border-amber/60'
            : 'hover:bg-panelHi border border-transparent')
        }
        style={{ paddingLeft: `${0.25 + depth * 0.75}rem` }}
      >
        <button
          onClick={complete}
          disabled={busy}
          aria-label="mark complete"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-borderHi shrink-0 hover:border-green hover:bg-green/20 transition-colors"
          title="mark complete"
        >
          {busy && <span className="text-[9px]">·</span>}
        </button>
        <button
          onClick={() => onSelect({ type: 'task', id: task.id })}
          className={
            'flex-1 text-left min-w-0 py-1 ' +
            (selected ? 'text-white' : 'text-muted hover:text-ink')
          }
        >
          <span className="truncate text-[11px] inline-block max-w-full">{task.title}</span>
        </button>
        {task.status === 'blocked' && (
          <span className="w-1.5 h-1.5 rounded-full bg-red shrink-0 mr-1" />
        )}
      </div>
      {activeKids.length > 0 && (
        <div>
          {activeKids.map((k) => (
            <TaskLeaf
              key={k.id}
              task={k}
              selection={selection}
              childrenByParent={childrenByParent}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
